import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Label, Select, Button, InputField, QuestionField, DeleteButton } from '../styling/StyledComponents';
import Navbar from '../Components/Navigation/Navbar';
import { CoordsData } from '../Geolocation/CoordsProvider';
import CoordsMap from '../MapComponents/CoordsMap';
import Footer from '../Components/Footer/Footer';
import StaticColorIndicator from '../Components/Color/StaticColorIndicator';
import PopupMessage from '../Components/PopupMessage';
import Cookies from 'js-cookie';

function QuestionManagement() {
    const [coords, setCoords] = CoordsData();
    // State for Add Question form
    const [questionData, setQuestionData] = useState({
        question: '',
        maxRating: 5,
        minRating: 1,
        maxText: 'Excellent',
        minText: 'Poor',
        radius: 2,
        isPublic: false,
        projectId: ''
    });

    // State for Edit Question form
    const [selectedQuestion, setSelectedQuestion] = useState({
        id: -1,
        question: '',
        max_rating: 5,
        min_rating: 1,
        max_text: 'Excellent',
        min_text: 'Poor',
        radius: 2,
        isGeneral: false,
        project_id: ''
    });

    const [questions, setQuestions] = useState([]);
    const [filteredQuestions, setFilteredQuestions] = useState([]);
    const [projects, setProjects] = useState([]);
    const [selectedProjectId, setSelectedProjectId] = useState('');
    const [activeQuestionId, setActiveQuestionId] = useState(null); // State to track the active question

    // Popup message states
    const [deletePopup, setDeletePopup] = useState({
        show: false,
        questionId: null,
        questionText: ''
    });

    const [successPopup, setSuccessPopup] = useState({
        show: false,
        message: ''
    });

    const getProjects = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/projects`, {
                headers: {
                    Authorization: `Bearer ${Cookies.get('token')}`
                },
                withCredentials: true
            });
            if (response.data.success) {
                setProjects(response.data.result);
            }
        } catch (error) {
            console.error("Error fetching projects:", error);
            setSuccessPopup({
                show: true,
                message: 'Error fetching projects. Please try again.'
            });
        }
    };

    // Fetch all questions
    const getQuestions = async () => {
        try {
            const data = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/questions`)
                .then(res => res.data);

            if (data.success) {
                setQuestions(data.result);
                setFilteredQuestions(data.result);

                const active = data.result.find(q => q.isActive);
                if (active) {
                    setActiveQuestionId(active.id);
                }
            }
        } catch (error) {
            console.error('Failed to fetch questions:', error);
        }
    };


    useEffect(() => {
        getQuestions();
        getProjects();
    }, []);

    // Filter questions based on selected project
    useEffect(() => {
        if (selectedProjectId) {
            const filtered = questions.filter(question =>
                question.project_id == selectedProjectId
            );
            setFilteredQuestions(filtered);
        } else {
            setFilteredQuestions(questions); // Show all questions if no project is selected
        }
    }, [selectedProjectId, questions]);

    // Handlers for Add Question form
    const handleAddInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'projectId') {
            setSelectedProjectId(value);
        }
        setQuestionData({
            ...questionData,
            [name]: value
        });
    };

    const handleAddSubmit = async () => {
        try {
            questionData.coords = coords;
            const data = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/question`,
                {
                    ...questionData,
                    projectId: questionData.projectId,
                    isGeneral: questionData.isPublic
                },
                {
                    headers: {
                        Authorization: `Bearer ${Cookies.get('token')}`
                    },
                    withCredentials: true
                }).then(res => res.data);

            if (data.success) {
                setSuccessPopup({
                    show: true,
                    message: "Question added successfully!"
                });

                setQuestionData({
                    question: '',
                    maxRating: 5,
                    minRating: 1,
                    maxText: 'Excellent',
                    minText: 'Poor',
                    radius: 2,
                    isPublic: false,
                    projectId: selectedProjectId // Keep the same project selected
                });
                getQuestions();
            }
        } catch (error) {
            console.error('Error inserting question:', error);
            setSuccessPopup({
                show: true,
                message: "Error adding question. Please try again."
            });
        }
    };

    // Handlers for Edit Question form
    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setSelectedQuestion(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEditSubmit = async () => {
        try {
            const data = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/question/${selectedQuestion.id}`, {
                question: selectedQuestion.question,
                max_rating: selectedQuestion.max_rating,
                min_rating: selectedQuestion.min_rating,
                max_text: selectedQuestion.max_text,
                min_text: selectedQuestion.min_text,
                coordinates: {
                    lat: coords.lat,
                    lng: coords.lng
                },
                radius: selectedQuestion.radius,
                isGeneral: selectedQuestion.isGeneral,
                project_id: selectedQuestion.project_id
            },
                {
                    headers: {
                        Authorization: `Bearer ${Cookies.get('token')}`
                    },
                    withCredentials: true
                }).then(res => res.data);

            if (data.success) {
                setSuccessPopup({
                    show: true,
                    message: "Question updated successfully!"
                });
                getQuestions();
            } else {
                setSuccessPopup({
                    show: true,
                    message: "Failed to update question"
                });
            }
        } catch (error) {
            console.error('Error updating question:', error);
            setSuccessPopup({
                show: true,
                message: "Error updating question. Please try again."
            });
        }
    };


    // Function to handle setting an active question
    const handleSetActiveQuestion = async (questionId) => {
        try {
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/question/${questionId}/activate`, {}, {
                headers: {
                    Authorization: `Bearer ${Cookies.get('token')}`
                },
                withCredentials: true
            });
    
            if (response.data && response.data.success) {
                setActiveQuestionId(questionId);
                setSuccessPopup({
                    show: true,
                    message: "Question activated successfully!"
                });
                getQuestions();
            } else {
                setSuccessPopup({
                    show: true,
                    message: "Failed to activate question."
                });
            }
        } catch (err) {
            console.error('Failed to activate question:', err);
            setSuccessPopup({
                show: true,
                message: "Error activating question. Please try again."
            });
        }
    };
    



    // Shows delete confirmation popup
    const showDeleteConfirmation = (question) => {
        setDeletePopup({
            show: true,
            questionId: question.id,
            questionText: question.question
        });
    };

    // Function to handle deletion of a question
    const confirmDeleteQuestion = async () => {
        try {
            const data = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/question/${deletePopup.questionId}`, {
                headers: {
                    Authorization: `Bearer ${Cookies.get('token')}`
                },
                withCredentials: true
            }).then(res => res.data);

            // Close the popup
            setDeletePopup({
                show: false,
                questionId: null,
                questionText: ''
            });

            // Update questions state and handle active question logic
            setQuestions(prevQuestions => {
                const updatedQuestions = prevQuestions.filter(question => question.id !== deletePopup.questionId);

                // If the deleted question was active, set the first remaining question as active
                if (activeQuestionId === deletePopup.questionId) {
                    if (updatedQuestions.length > 0) {
                        handleSetActiveQuestion(updatedQuestions[0].id);
                    } else {
                        localStorage.removeItem('activeQuestionId');
                    }
                }

                return updatedQuestions;
            });

            setSuccessPopup({
                show: true,
                message: 'Question deleted successfully.'
            });
        } catch (error) {
            console.error("Failed to delete question", error);
            setDeletePopup({ show: false, questionId: null, questionText: '' });

            setSuccessPopup({
                show: true,
                message: 'Failed to delete question. Please try again.'
            });
        }
    };

    return (
        <div className='fill-height flex-column between'>
            <Navbar />
            <Label className='padding-m center'>Question Management</Label>

            <div className='flex-column create-view fill-width-edit'>
                <div className='flex-column m-margin'>
                    <Select
                        name="projectId"
                        value={selectedProjectId}
                        onChange={handleAddInputChange}
                        required
                    >
                        <option value="">Select a Project (Show all questions)</option>
                        {projects.map(project => (
                            <option key={project.id} value={project.id}>
                                {project.name}
                            </option>
                        ))}
                    </Select>
                </div>
            </div>
            <div className='padding-m'></div>

            {/* Add Question Form */}
            <div className='flex-column create-view fill-width-edit'>
                <Label className='padding-m'>Add New Question</Label>
                <div className='m-margin'>
                    <div className='flex-row right'>
                        <StaticColorIndicator
                            diff_rating={questionData.maxRating - questionData.minRating}
                            min_rating={questionData.minRating}
                        />
                    </div>
                    {!questionData.isPublic && (
                        <CoordsMap radius={questionData.radius} />
                    )}
                </div>

                <div className='flex-column'>
                    <div className='flex-column m-margin'>
                        <Label>Question</Label>
                        <QuestionField
                            type="text"
                            name="question"
                            value={questionData.question}
                            onChange={handleAddInputChange}
                        />
                    </div>
                    <div className='center flex-edit-field'>
                        <div className='flex-column m-margin'>
                            <Label>Max Rating</Label>
                            <InputField
                                type="number"
                                name="maxRating"
                                value={questionData.maxRating}
                                onChange={handleAddInputChange}
                            />
                            <Label>Min Rating</Label>
                            <InputField
                                type="number"
                                name="minRating"
                                value={questionData.minRating}
                                onChange={handleAddInputChange}
                            />
                        </div>

                        <div className='flex-column m-margin'>
                            <Label>Max Text</Label>
                            <InputField
                                type="text"
                                name="maxText"
                                value={questionData.maxText}
                                onChange={handleAddInputChange}
                            />
                            <Label>Min Text</Label>
                            <InputField
                                type="text"
                                name="minText"
                                value={questionData.minText}
                                onChange={handleAddInputChange}
                            />
                        </div>

                        <div className='flex-column m-margin'>
                            {!questionData.isPublic && (
                                <>
                                    <Label>Radius (in kilometers)</Label>
                                    <InputField
                                        type="number"
                                        name="radius"
                                        value={questionData.radius}
                                        onChange={handleAddInputChange}
                                    />
                                </>
                            )}

                            <Label>Is the question global?</Label>
                            <Select
                                name="isPublic"
                                value={questionData.isPublic}
                                onChange={e => setQuestionData({
                                    ...questionData,
                                    isPublic: e.target.value === "true"
                                })}
                            >
                                <option value={true}>Yes</option>
                                <option value={false}>No</option>
                            </Select>
                        </div>
                    </div>

                    <Button onClick={handleAddSubmit} type="submit">Add Question</Button>
                </div>
            </div>

            {/* List of All Questions */}
            <div className='center flex-column padding-m'>
                <Label className='padding-m'>All Questions</Label>
                <div className="flex-column fill-width-edit">
                    {filteredQuestions.map((question) => (
                        <div key={question.id} className="flex-row m-margin between align-center question-row">
                            <div className="flex-column">
                                <Label>{question.question}</Label>
                            </div>
                            <div className="flex-row">
                                <Button
                                    className={`m-margin ${activeQuestionId === question.id ? 'active-btn' : 'inactive-btn'}`}
                                    onClick={() => handleSetActiveQuestion(question.id)}
                                    style={{
                                        backgroundColor: activeQuestionId === question.id ? '#4CAF50' : '#f44336',
                                        color: 'white',
                                        marginRight: '10px'
                                    }}
                                >
                                    {activeQuestionId === question.id ? 'Activated' : 'deactivated'}
                                </Button>
                                <Button
                                    className="m-margin"
                                    onClick={() => setSelectedQuestion(question)}
                                >
                                    Edit
                                </Button>
                                <DeleteButton
                                    className="m-margin"
                                    onClick={() => showDeleteConfirmation(question)}
                                >
                                    Delete
                                </DeleteButton>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Edit Question Form */}
            {selectedQuestion.id !== -1 && (
                <div className='flex-column create-view fill-width-edit'>
                    <Label className='padding-m'>Edit Question</Label>
                    <div className='m-margin'>
                        <div className='flex-row right'>
                            <StaticColorIndicator
                                diff_rating={selectedQuestion.max_rating - selectedQuestion.min_rating}
                                min_rating={selectedQuestion.min_rating}
                            />
                        </div>
                        {!selectedQuestion.isGeneral && (
                            <CoordsMap radius={selectedQuestion.radius} />
                        )}
                    </div>

                    <div className='flex-column'>
                        <div className='flex-column m-margin'>
                            <Label>Question</Label>
                            <QuestionField
                                type="text"
                                name="question"
                                value={selectedQuestion.question}
                                onChange={handleEditInputChange}
                            />
                        </div>
                        <div className='center flex-edit-field'>
                            <div className='flex-column m-margin'>
                                <Label>Max Rating</Label>
                                <InputField
                                    type="number"
                                    name="max_rating"
                                    value={selectedQuestion.max_rating}
                                    onChange={handleEditInputChange}
                                />
                                <Label>Min Rating</Label>
                                <InputField
                                    type="number"
                                    name="min_rating"
                                    value={selectedQuestion.min_rating}
                                    onChange={handleEditInputChange}
                                />
                            </div>

                            <div className='flex-column m-margin'>
                                <Label>Max Text</Label>
                                <InputField
                                    type="text"
                                    name="max_text"
                                    value={selectedQuestion.max_text}
                                    onChange={handleEditInputChange}
                                />
                                <Label>Min Text</Label>
                                <InputField
                                    type="text"
                                    name="min_text"
                                    value={selectedQuestion.min_text}
                                    onChange={handleEditInputChange}
                                />
                            </div>

                            <div className='flex-column m-margin'>
                                {!selectedQuestion.isGeneral && (
                                    <>
                                        <Label>Radius (in kilometers)</Label>
                                        <InputField
                                            type="number"
                                            name="radius"
                                            value={selectedQuestion.radius}
                                            onChange={handleEditInputChange}
                                        />
                                    </>
                                )}

                                <Label>Is the question global?</Label>
                                <Select
                                    name="isGeneral"
                                    value={selectedQuestion.isGeneral}
                                    onChange={e => setSelectedQuestion({
                                        ...selectedQuestion,
                                        isGeneral: e.target.value === "true"
                                    })}
                                >
                                    <option value={true}>Yes</option>
                                    <option value={false}>No</option>
                                </Select>
                            </div>
                        </div>

                        <Button onClick={handleEditSubmit} type="submit">Update Question</Button>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Popup */}
            <PopupMessage
                show={deletePopup.show}
                title="Confirm Deletion"
                message={`Are you sure you want to delete the question "${deletePopup.questionText}"? All associated answers will be removed. This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={confirmDeleteQuestion}
                onCancel={() => setDeletePopup({ show: false, questionId: null, questionText: '' })}
                isDanger={true}
            />

            {/* Success/Error Popup */}
            <PopupMessage
                show={successPopup.show}
                title="Notification"
                message={successPopup.message}
                confirmText="OK"
                onConfirm={() => setSuccessPopup({ show: false, message: '' })}
                onCancel={() => setSuccessPopup({ show: false, message: '' })}
            />

            <div className='padding-m'></div>
            <Footer />
        </div>
    );
}

export default QuestionManagement;